/**
 * ComparisonMetrics - Week-over-week and month-over-month comparison metrics
 * 
 * Features:
 * - Display percentage changes for active, inactive, and total users
 * - Color coding: green for positive trends, red for negative
 * - Tooltips explaining calculation methodology
 * - Responsive design for mobile, tablet, and desktop
 * 
 * Requirements: 2.16, 2.17
 */

import React from 'react';
import './ComparisonMetrics.css';

const ComparisonMetrics = ({ comparison, loading = false, className = '' }) => {
  // Loading state
  if (loading) {
    return (
      <div className={`comparison-metrics comparison-metrics--loading ${className}`}>
        <div className="comparison-metrics__card comparison-metrics__card--skeleton">
          <div className="comparison-metrics__skeleton-label"></div>
          <div className="comparison-metrics__skeleton-value"></div>
        </div>
        <div className="comparison-metrics__card comparison-metrics__card--skeleton">
          <div className="comparison-metrics__skeleton-label"></div>
          <div className="comparison-metrics__skeleton-value"></div>
        </div>
        <div className="comparison-metrics__card comparison-metrics__card--skeleton">
          <div className="comparison-metrics__skeleton-label"></div>
          <div className="comparison-metrics__skeleton-value"></div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!comparison) {
    return null;
  }

  // Format metric for display
  const formatMetric = (change, label) => {
    const value = Math.abs(change);
    const sign = change > 0 ? '+' : change < 0 ? '-' : '';
    const color = change > 0 ? 'success' : change < 0 ? 'error' : 'neutral';
    const text = `${sign}${value.toFixed(1)}%`;
    const description = `${label} vs last ${comparison.period}`;
    
    return { value: change, text, color, sign, description };
  };

  const metrics = {
    active: formatMetric(comparison.activeChange, 'Active users'),
    inactive: formatMetric(comparison.inactiveChange, 'Inactive users'),
    total: formatMetric(comparison.totalChange, 'Total users')
  };

  // Tooltip content explaining calculation methodology
  const getTooltipContent = (metricType) => {
    const periodLabel = comparison.period === 'week' ? 'week' : 'month';
    const periodDays = comparison.period === 'week' ? '7 days' : '30 days';
    
    switch (metricType) {
      case 'active':
        return `Percentage change in active users (last login ≤7 days) compared to the previous ${periodLabel}. Calculated as: ((current ${periodDays} average - previous ${periodDays} average) / previous ${periodDays} average) × 100`;
      case 'inactive':
        return `Percentage change in inactive users (last login 8-30 days) compared to the previous ${periodLabel}. Calculated as: ((current ${periodDays} average - previous ${periodDays} average) / previous ${periodDays} average) × 100`;
      case 'total':
        return `Percentage change in total users compared to the previous ${periodLabel}. Calculated as: ((current ${periodDays} average - previous ${periodDays} average) / previous ${periodDays} average) × 100`;
      default:
        return '';
    }
  };

  return (
    <div className={`comparison-metrics ${className}`} role="region" aria-label="Activity comparison metrics">
      {/* Active Users Metric */}
      <div className={`comparison-metrics__card comparison-metrics__card--${metrics.active.color}`}>
        <div className="comparison-metrics__icon" aria-hidden="true">
          {metrics.active.sign === '+' ? '↑' : metrics.active.sign === '-' ? '↓' : '→'}
        </div>
        <div className="comparison-metrics__content">
          <div className="comparison-metrics__value">
            {metrics.active.text}
          </div>
          <div className="comparison-metrics__label">
            {metrics.active.description}
          </div>
        </div>
        <button
          className="comparison-metrics__tooltip-trigger"
          aria-label="Explanation of active users calculation"
          title={getTooltipContent('active')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.93 12.5h-1.86v-1.86h1.86v1.86zm-.93-3.14c-.74 0-1.34-.6-1.34-1.34V6.68c0-.74.6-1.34 1.34-1.34s1.34.6 1.34 1.34v1.34c0 .74-.6 1.34-1.34 1.34z"/>
          </svg>
        </button>
      </div>

      {/* Inactive Users Metric */}
      <div className={`comparison-metrics__card comparison-metrics__card--${metrics.inactive.color}`}>
        <div className="comparison-metrics__icon" aria-hidden="true">
          {metrics.inactive.sign === '+' ? '↑' : metrics.inactive.sign === '-' ? '↓' : '→'}
        </div>
        <div className="comparison-metrics__content">
          <div className="comparison-metrics__value">
            {metrics.inactive.text}
          </div>
          <div className="comparison-metrics__label">
            {metrics.inactive.description}
          </div>
        </div>
        <button
          className="comparison-metrics__tooltip-trigger"
          aria-label="Explanation of inactive users calculation"
          title={getTooltipContent('inactive')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.93 12.5h-1.86v-1.86h1.86v1.86zm-.93-3.14c-.74 0-1.34-.6-1.34-1.34V6.68c0-.74.6-1.34 1.34-1.34s1.34.6 1.34 1.34v1.34c0 .74-.6 1.34-1.34 1.34z"/>
          </svg>
        </button>
      </div>

      {/* Total Users Metric */}
      <div className={`comparison-metrics__card comparison-metrics__card--${metrics.total.color}`}>
        <div className="comparison-metrics__icon" aria-hidden="true">
          {metrics.total.sign === '+' ? '↑' : metrics.total.sign === '-' ? '↓' : '→'}
        </div>
        <div className="comparison-metrics__content">
          <div className="comparison-metrics__value">
            {metrics.total.text}
          </div>
          <div className="comparison-metrics__label">
            {metrics.total.description}
          </div>
        </div>
        <button
          className="comparison-metrics__tooltip-trigger"
          aria-label="Explanation of total users calculation"
          title={getTooltipContent('total')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.93 12.5h-1.86v-1.86h1.86v1.86zm-.93-3.14c-.74 0-1.34-.6-1.34-1.34V6.68c0-.74.6-1.34 1.34-1.34s1.34.6 1.34 1.34v1.34c0 .74-.6 1.34-1.34 1.34z"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ComparisonMetrics;
